import { useEffect, useState } from 'react';

interface OrderItem {
  name: string;
  qty: number;
  price: number;
  image?: string;
}

interface OrderBillProps {
  orderId: string;
  date: string;
  items: OrderItem[];
  total: number;
  status: 'delivered' | 'in-transit' | 'cancelled' | 'out-for-delivery' | 'confirmed' | 'pending';
  onClose: () => void;
}

const ITEM_PRICES: Record<string, number> = {
  'Premium Polo Shirt': 1299,
  'Chino Pants': 900,
  'Midnight Black T-Shirt': 799,
  'Navy Elite T-Shirt': 799,
  'Classic White Tee': 799,
  'Urban Grey Hoodie': 1499,
  'Black Joggers': 999,
  'Olive Cargo Shorts': 999,
  'Slim Fit Jeans': 1099,
  'Graphic Tee': 700,
  'Striped Summer Shirt': 849,
};

function buildLineItems(items: OrderItem[], total: number): OrderItem[] {
  return items.map((item) => ({
    ...item,
    price: item.price > 0 ? item.price : (ITEM_PRICES[item.name] ?? Math.round(total / Math.max(items.length, 1))),
  }));
}

function getDiscount(subtotal: number): number {
  return subtotal >= 1500 ? Math.round(subtotal * 0.05) : 0;
}

export default function OrderBillModal({ orderId, date, items, total, status, onClose }: OrderBillProps) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const lineItems = buildLineItems(items, total);
  const subtotal = lineItems.reduce((s, i) => s + i.price * i.qty, 0);
  const discount = getDiscount(subtotal);
  // No delivery charge, no platform fee
  const grandTotal = subtotal > 0 ? subtotal - discount : total;

  const statusConfig: Record<string, { color: string; icon: string; label: string }> = {
    delivered: { color: 'text-green-600 bg-green-50 border-green-200', icon: 'ri-checkbox-circle-fill', label: 'Delivered' },
    'out-for-delivery': { color: 'text-orange-600 bg-orange-50 border-orange-200', icon: 'ri-e-bike-2-fill', label: 'Out for Delivery' },
    'in-transit': { color: 'text-amber-600 bg-amber-50 border-amber-200', icon: 'ri-time-fill', label: 'In Transit' },
    confirmed: { color: 'text-sky-600 bg-sky-50 border-sky-200', icon: 'ri-checkbox-circle-line', label: 'Confirmed' },
    pending: { color: 'text-gray-600 bg-gray-50 border-gray-200', icon: 'ri-time-line', label: 'Pending' },
    cancelled: { color: 'text-red-600 bg-red-50 border-red-200', icon: 'ri-close-circle-fill', label: 'Cancelled' },
  };
  const sc = statusConfig[status] ?? statusConfig.confirmed;

  const formattedDate = new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(orderId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getBillHTML = () => {
    const isPaid = status === 'delivered';
    const itemRows = lineItems.map((item) => `
      <tr>
        <td style="padding:10px 12px;font-size:13px;color:#374151;border-bottom:1px solid #f3f4f6;">${item.name}</td>
        <td style="padding:10px 12px;font-size:13px;color:#374151;text-align:center;border-bottom:1px solid #f3f4f6;">${item.qty}</td>
        <td style="padding:10px 12px;font-size:13px;color:#111827;font-weight:600;text-align:right;border-bottom:1px solid #f3f4f6;">&#8377;${(item.price * item.qty).toLocaleString('en-IN')}</td>
      </tr>`).join('');

    const discountRow = discount > 0 ? `
      <tr>
        <td style="padding:5px 0;font-size:13px;color:#16a34a;">Discount (5% off)</td>
        <td style="padding:5px 0;font-size:13px;color:#16a34a;text-align:right;font-weight:500;">-&#8377;${discount.toLocaleString('en-IN')}</td>
      </tr>` : '';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Invoice - ${orderId}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Segoe UI',Arial,sans-serif;background:#f9fafb;display:flex;justify-content:center;padding:30px 16px;}
    .invoice{background:#fff;max-width:480px;width:100%;border-radius:16px;overflow:hidden;border:1px solid #e5e7eb;}
    @media print{body{background:#fff;padding:0;} .invoice{border:none;border-radius:0;max-width:100%;} .no-print{display:none;}}
  </style>
</head>
<body>
<div class="invoice">
  <div style="background:linear-gradient(to right,#f97316,#ef4444);padding:20px;color:#fff;">
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="width:38px;height:38px;background:rgba(255,255,255,0.2);border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:20px;">&#128248;</div>
        <div><div style="font-weight:700;font-size:16px;">ClothyBazar</div><div style="opacity:.75;font-size:11px;">Dehradun, Uttarakhand</div></div>
      </div>
      <div style="background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);border-radius:999px;padding:4px 10px;font-size:11px;font-weight:600;">${sc.label}</div>
    </div>
    <div style="border-top:1px solid rgba(255,255,255,0.2);padding-top:12px;display:flex;justify-content:space-between;">
      <div><div style="opacity:.7;font-size:11px;margin-bottom:3px;">Invoice No.</div><div style="font-family:monospace;font-weight:700;font-size:13px;">${orderId}</div></div>
      <div style="text-align:right;"><div style="opacity:.7;font-size:11px;margin-bottom:3px;">Date</div><div style="font-weight:500;font-size:13px;">${formattedDate}</div></div>
    </div>
  </div>
  <div style="padding:0 20px;">
    <table style="width:100%;border-collapse:collapse;margin-top:4px;">
      <thead><tr style="background:#f9fafb;">
        <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#9ca3af;text-align:left;border-bottom:1px solid #e5e7eb;">Item</th>
        <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#9ca3af;text-align:center;border-bottom:1px solid #e5e7eb;">Qty</th>
        <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#9ca3af;text-align:right;border-bottom:1px solid #e5e7eb;">Amount</th>
      </tr></thead>
      <tbody>${itemRows}</tbody>
    </table>
  </div>
  <div style="padding:14px 20px;border-top:1px solid #f3f4f6;">
    <table style="width:100%;border-collapse:collapse;">
      <tr><td style="padding:5px 0;font-size:13px;color:#6b7280;">Subtotal</td><td style="padding:5px 0;font-size:13px;text-align:right;font-weight:500;">&#8377;${subtotal.toLocaleString('en-IN')}</td></tr>
      ${discountRow}
      <tr style="border-top:2px solid #e5e7eb;">
        <td style="padding:10px 0 5px;font-size:15px;color:#111827;font-weight:700;">Total Amount</td>
        <td style="padding:10px 0 5px;font-size:18px;color:#111827;font-weight:700;text-align:right;">&#8377;${grandTotal.toLocaleString('en-IN')}</td>
      </tr>
    </table>
  </div>
  <div style="padding:14px 20px;background:#f9fafb;border-top:1px solid #f3f4f6;">
    <div style="display:flex;align-items:center;justify-content:space-between;">
      <div><div style="font-weight:600;font-size:13px;color:#111827;">Cash on Delivery</div><div style="font-size:11px;color:#9ca3af;margin-top:2px;">Paid at door</div></div>
      <div style="background:${status === 'cancelled' ? '#fef2f2' : isPaid ? '#f0fdf4' : '#fffbeb'};border:1px solid ${status === 'cancelled' ? '#fecaca' : isPaid ? '#bbf7d0' : '#fde68a'};color:${status === 'cancelled' ? '#dc2626' : isPaid ? '#16a34a' : '#d97706'};border-radius:999px;padding:4px 10px;font-size:11px;font-weight:700;">${status === 'cancelled' ? '&#10005; Cancelled' : isPaid ? '&#10003; Paid' : '&#9201; Pay on Delivery'}</div>
    </div>
  </div>
  <div style="padding:14px 20px;text-align:center;border-top:1px solid #f3f4f6;">
    <div style="font-size:11px;color:#9ca3af;">Thank you for shopping with ClothyBazar!</div>
  </div>
</div>
</body></html>`;
  };

  const handleDownload = () => {
    const html = getBillHTML();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice-${orderId}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const handlePrint = () => {
    const html = getBillHTML();
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.onload = () => { win.print(); };
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center transition-all duration-300 ${visible ? 'bg-black/55' : 'bg-transparent'} backdrop-blur-sm`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className={`bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl flex flex-col max-h-[93vh] overflow-hidden transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full md:translate-y-8'}`}
      >
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-300"></div>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
              <i className="ri-file-list-3-line text-orange-500 text-lg"></i>
            </div>
            <div>
              <h2 className="text-gray-900 font-bold text-base">Order Invoice</h2>
              <p className="text-gray-400 text-xs mt-0.5">Bill of Supply</p>
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Brand Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                  <i className="ri-t-shirt-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="font-bold text-base">ClothyBazar</p>
                  <p className="text-white/75 text-xs">Dehradun, Uttarakhand</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold bg-white/15 border-white/30">
                <i className={sc.icon}></i>{sc.label}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/20 flex items-center justify-between">
              <div>
                <p className="text-white/70 text-xs">Invoice No.</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <p className="font-mono font-bold text-sm">{orderId}</p>
                  <button onClick={handleCopy} className="w-5 h-5 flex items-center justify-center hover:bg-white/20 rounded transition-colors cursor-pointer">
                    <i className={`text-xs ${copied ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                  </button>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-xs">Date</p>
                <p className="font-medium text-sm mt-0.5">{formattedDate}</p>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-2">
                <p className="col-span-7 text-xs font-semibold text-gray-500 uppercase tracking-wide">Item</p>
                <p className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Qty</p>
                <p className="col-span-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Amount</p>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {lineItems.map((item, idx) => (
                <div key={idx} className="px-4 py-3 grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-7 flex items-center gap-2.5">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-10 h-12 object-cover object-top rounded-lg shrink-0 bg-gray-100" />
                    ) : (
                      <div className="w-10 h-12 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                        <i className="ri-t-shirt-2-line text-orange-400 text-sm"></i>
                      </div>
                    )}
                    <p className="text-gray-800 text-xs font-medium leading-tight">{item.name}</p>
                  </div>
                  <p className="col-span-2 text-gray-600 text-xs text-center">{item.qty}</p>
                  <p className="col-span-3 text-gray-900 text-xs font-semibold text-right">&#8377;{(item.price * item.qty).toLocaleString('en-IN')}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Price Breakdown — no delivery charge, no platform fee */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 space-y-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Subtotal ({lineItems.length} item{lineItems.length > 1 ? 's' : ''})</span>
                <span className="text-gray-800 font-medium">&#8377;{subtotal.toLocaleString('en-IN')}</span>
              </div>
              {discount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-600 flex items-center gap-1">
                    <i className="ri-price-tag-3-line text-xs"></i>Discount (5% off)
                  </span>
                  <span className="text-green-600 font-medium">-&#8377;{discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="pt-2.5 border-t border-gray-200 flex items-center justify-between">
                <span className="text-gray-900 font-bold text-base">Total Amount</span>
                <span className="text-gray-900 font-bold text-xl">&#8377;{grandTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>
            {discount > 0 && (
              <div className="px-4 py-2.5 bg-green-50 border-t border-green-100 flex items-center gap-2">
                <i className="ri-shield-check-fill text-green-500 text-sm shrink-0"></i>
                <p className="text-green-700 text-xs font-medium">You saved &#8377;{discount} on this order!</p>
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2.5">Payment Method</p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                <i className="ri-cash-line text-gray-500"></i>
              </div>
              <div>
                <p className="text-gray-900 font-medium text-sm">Cash on Delivery</p>
                <p className="text-gray-400 text-xs">Paid at door</p>
              </div>
              <div className="ml-auto">
                <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-semibold ${
                  status === 'cancelled'
                    ? 'text-red-600 bg-red-50 border-red-200'
                    : status === 'delivered'
                    ? 'text-green-600 bg-green-50 border-green-200'
                    : 'text-amber-600 bg-amber-50 border-amber-200'
                }`}>
                  <i className={
                    status === 'cancelled' ? 'ri-close-line' :
                    status === 'delivered' ? 'ri-check-line' :
                    'ri-time-line'
                  }></i>
                  {status === 'cancelled' ? 'Cancelled' : status === 'delivered' ? 'Paid' : 'Pay on Delivery'}
                </span>
              </div>
            </div>
          </div>

          <div className="text-center py-1">
            <p className="text-gray-400 text-xs">Thank you for shopping with ClothyBazar!</p>
          </div>
          <div className="h-2"></div>
        </div>

        {/* Bottom actions — Download + Print */}
        <div className="px-5 py-4 border-t border-gray-100 bg-white shrink-0 flex gap-2">
          <button onClick={handleClose} className="px-4 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
            Close
          </button>
          <button onClick={handlePrint} className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
            <i className="ri-printer-line"></i>Print
          </button>
          <button onClick={handleDownload} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
            <i className="ri-download-2-line"></i>Download
          </button>
        </div>
      </div>
    </div>
  );
}
